import {
  type CollectionCardConfig,
  type CollectionCardField,
  type CollectionItem,
  findCardTemplateBlock,
  resolveCardConfigFromTemplate,
  resolveSymmetricCarouselDisplay,
} from '@/lib/blocks/collection/blocks';
import { isCollectionItemLike, readCollectionItems } from '@/lib/blocks/collection/item-utils';
import {
  type DataWidgetRenderer as DataWidgetLayoutRenderer,
  getDataWidgetVariants,
  getVariantEntry,
} from '@/lib/blocks/collection/variant-registry';
import { readRecord, readString } from '@/lib/prop-utils';
import type { BlockType, PageBlock } from '@/types/block';

export type DataWidgetBlockType = 'blog_widget' | 'case_result_widget' | 'review_widget';
export type DataWidgetVariant = 'grid' | 'alternating' | 'carousel' | 'accordion';
export type DataWidgetSourceMode = 'dynamic' | 'static';
export type DataWidgetStrategy = 'newest' | 'pinned_only' | 'pinned_first' | 'sorted';

export type DataWidgetFilterSelection = 'all' | string[];
export type DataWidgetFilters = Record<string, DataWidgetFilterSelection>;

export function hasActiveDataWidgetFilters(filters: DataWidgetFilters | undefined): boolean {
  if (!filters) return false;

  return Object.values(filters).some((selection) => selection !== 'all' && selection.length > 0);
}

export interface DataWidgetSourceConfig {
  mode: DataWidgetSourceMode;
  strategy: DataWidgetStrategy;
  limit: number;
  filters: DataWidgetFilters;
}

export interface DataWidgetProps {
  source: DataWidgetSourceConfig;
  variantSet: string;
  variant: DataWidgetVariant;
  variantProps: Record<string, Record<string, unknown>>;
  items: CollectionItem[];
  staticItems: CollectionItem[];
}

export interface DataWidgetVariantEntry {
  key: DataWidgetVariant;
  label: string;
  renderer: DataWidgetLayoutRenderer;
  defaultProps: Record<string, unknown>;
}

export const DATA_WIDGET_VARIANTS = getDataWidgetVariants() as DataWidgetVariantEntry[];

export function isDataWidgetBlock(block: Pick<PageBlock, 'blockType'>): boolean {
  return isDataWidgetBlockType(block.blockType);
}

export function isDataWidgetBlockType(blockType: BlockType): blockType is DataWidgetBlockType {
  return (
    blockType === 'blog_widget' ||
    blockType === 'case_result_widget' ||
    blockType === 'review_widget'
  );
}

export function getDataWidgetLabel(blockType: DataWidgetBlockType): string {
  switch (blockType) {
    case 'blog_widget':
      return 'Blog Posts';
    case 'case_result_widget':
      return 'Case Results';
    case 'review_widget':
      return 'Reviews';
  }
}

const DATA_WIDGET_METADATA_SLOTS = new Map<DataWidgetBlockType, readonly string[]>([
  ['blog_widget', ['image', 'topics', 'authors', 'publishedAt']],
  ['case_result_widget', ['attorneys', 'court', 'practiceAreas']],
  ['review_widget', ['rating', 'reviewerIcon']],
]);

export function isDataWidgetMetadataSlot(
  blockType: DataWidgetBlockType,
  slot: string | undefined,
): boolean {
  if (!slot) return false;

  return DATA_WIDGET_METADATA_SLOTS.get(blockType)?.includes(slot) ?? false;
}

export function filterDataWidgetFieldsForSourceMode(
  blockType: DataWidgetBlockType,
  fields: CollectionCardField[],
  sourceMode: DataWidgetSourceMode,
): CollectionCardField[] {
  if (sourceMode === 'dynamic') {
    return fields;
  }

  return fields.filter(
    (field) =>
      !isDataWidgetMetadataSlot(blockType, field.slot) ||
      (blockType === 'blog_widget' && field.slot === 'image'),
  );
}

function readLimit(value: unknown): number {
  const limit = Number(value);
  if (!Number.isFinite(limit) || limit <= 0) return 4;

  return Math.min(12, Math.trunc(limit));
}

function readFilters(value: unknown): DataWidgetFilters {
  const record = readRecord(value);
  if (!record) return {};

  const filters: DataWidgetFilters = {};
  for (const [slot, selection] of Object.entries(record)) {
    if (selection === 'all') {
      filters[slot] = 'all';
    } else if (Array.isArray(selection)) {
      filters[slot] = selection.filter((id): id is string => typeof id === 'string');
    }
  }
  return filters;
}

function readSource(value: unknown): DataWidgetSourceConfig {
  const source = readRecord(value) ?? {};
  const mode = readString(source.mode) === 'static' ? 'static' : 'dynamic';
  const strategyValue = readString(source.strategy);
  const strategy =
    strategyValue === 'pinned_only' ||
    strategyValue === 'pinned_first' ||
    strategyValue === 'sorted'
      ? strategyValue
      : 'newest';

  return {
    mode,
    strategy,
    limit: readLimit(source.limit),
    filters: readFilters(source.filters),
  };
}

/**
 * Derives staticItems from previewItems at insert time by stripping the metadata slots
 * specific to this block type. Metadata slots are hidden in static mode anyway, so they
 * don't need to be stored. This gives the section a meaningful static fallback when not
 * connected to a data source.
 */
export function deriveStaticItemsFromPreviewItems(
  blockType: DataWidgetBlockType,
  previewItems: unknown[],
): CollectionItem[] {
  const metadataSlots = new Set(DATA_WIDGET_METADATA_SLOTS.get(blockType) ?? []);

  return previewItems.filter(isCollectionItemLike).map((item) => {
    const staticItem: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(item)) {
      if (!metadataSlots.has(key) || (blockType === 'blog_widget' && key === 'image')) {
        staticItem[key] = value;
      }
    }
    return staticItem as CollectionItem;
  });
}

export function stripPreviewItemsFromBlock(block: PageBlock): PageBlock {
  if (!isDataWidgetBlock(block) || !block.props) return block;

  const { previewItems, ...props } = block.props;
  const hasStaticItems =
    Array.isArray(props.staticItems) && (props.staticItems as unknown[]).length > 0;
  const derivedStaticItems =
    !hasStaticItems && Array.isArray(previewItems) && isDataWidgetBlockType(block.blockType)
      ? deriveStaticItemsFromPreviewItems(block.blockType, previewItems as unknown[])
      : undefined;

  return {
    ...block,
    props: derivedStaticItems ? { ...props, staticItems: derivedStaticItems } : props,
  };
}

/**
 * Patches data widget blocks so the template preview modal renders rich preview items
 * instead of the sparse staticItems. Used by both the add-section layer and the page
 * template preview card.
 */
export function resolveTemplatePreviewBlocks(
  blocks: PageBlock[] | undefined,
): PageBlock[] | undefined {
  if (!blocks) return undefined;

  return blocks.map((block) => {
    if (!isDataWidgetBlock(block)) return block;

    const previewItems = Array.isArray(block.props?.previewItems)
      ? (block.props?.previewItems as unknown[])
      : [];
    if (previewItems.length === 0) return block;

    return {
      ...block,
      props: {
        ...(block.props ?? {}),
        source: {
          ...((block.props?.source as Record<string, unknown> | undefined) ?? {}),
          mode: 'dynamic',
        },
        items: previewItems,
        hasDynamicRecords: true,
      },
    };
  });
}

export function resolveDataWidgetProps(block: PageBlock): DataWidgetProps {
  const props = block.props || {};
  const variant = readString(props.variant) as DataWidgetVariant | null;
  const activeVariant = DATA_WIDGET_VARIANTS.some((entry) => entry.key === variant)
    ? (variant as DataWidgetVariant)
    : 'grid';

  return {
    source: readSource(props.source),
    variantSet: readString(props.variantSet) ?? block.blockType,
    variant: activeVariant,
    variantProps:
      (readRecord(props.variantProps) as Record<string, Record<string, unknown>> | null) ?? {},
    items: readCollectionItems(props.items),
    staticItems: readCollectionItems(props.staticItems),
  };
}

export function getDataWidgetVariantEntry(variant: string): DataWidgetVariantEntry {
  return (getVariantEntry(variant) as DataWidgetVariantEntry | null) ?? DATA_WIDGET_VARIANTS[0];
}

export function getDataWidgetVariantProps(props: DataWidgetProps): Record<string, unknown> {
  const entry = getDataWidgetVariantEntry(props.variant);
  return props.variantProps[props.variant] ?? entry.defaultProps;
}

export function resolveDataWidgetCardConfig(
  block: PageBlock,
  childrenByParent: Map<string, PageBlock[]>,
): {
  cardTemplateBlock?: PageBlock;
  cardTemplateChildren: PageBlock[];
  cardConfig: CollectionCardConfig;
} {
  const props = resolveDataWidgetProps(block);
  const variantSpecificProps = getDataWidgetVariantProps(props);
  const cardTemplateBlock =
    findCardTemplateBlock(childrenByParent.get(block.id) || []) ?? undefined;
  const cardTemplateChildren = cardTemplateBlock
    ? childrenByParent.get(cardTemplateBlock.id) || []
    : [];
  const cardConfig = cardTemplateBlock
    ? resolveCardConfigFromTemplate(cardTemplateBlock, cardTemplateChildren, variantSpecificProps)
    : { gap: 'md' as const, align: 'start' as const, fields: [] };

  return { cardTemplateBlock, cardTemplateChildren, cardConfig };
}

export function resolveDataWidgetCarouselDisplay(props: DataWidgetProps) {
  return resolveSymmetricCarouselDisplay(getDataWidgetVariantProps(props));
}
